import type { SerializedStyles, Theme } from '@emotion/react'
import { css } from '@emotion/react'

import {
  borderRadius,
  breakpoint,
  duration,
  easing,
  size,
} from '@twisto/styles'

import type { Size, Variant } from './index'

export const container = css`
  display: grid;
  grid-template-areas:
    'icon title arrow'
    'icon subtitle arrow';
  grid-template-columns: max-content auto max-content;
  grid-template-rows: max-content max-content;
  border-radius: ${borderRadius.md};
  transition: background-color ${easing.easeInOut} ${duration.standard}ms;

  & .banner-icon-wrapper {
    transform-style: preserve-3d;
    transition: background-color ${easing.easeInOut} ${duration.standard}ms;
  }

  & .banner-icon {
    transform-origin: center center;
    transition: transform ${easing.easeInOut} ${duration.standard}ms;
  }
`

export const containerHover = css`
  &:hover .banner-icon-wrapper {
    background-color: transparent;
  }

  &:hover .banner-icon {
    transform: scale3d(1.2, 1.2, 1);
  }
`

export const containerBackground: Record<
  Variant,
  (theme: Theme) => SerializedStyles
> = {
  primary: (theme) => css`
    background-color: ${theme.palette.brand[100]};
  `,
  secondary: (theme) => css`
    background-color: ${theme.palette.neutral[100]};
  `,
  warning: (theme) => css`
    background-color: ${theme.palette.warning[100]};
  `,
  error: (theme) => css`
    background-color: ${theme.palette.error[100]};
  `,
}

export const containerSize: Record<Size, SerializedStyles> = {
  small: css`
    padding: ${size[12]};

    ${breakpoint.up('sm')} {
      padding: ${size[16]};
    }
  `,
  medium: css`
    padding: ${size[12]};

    ${breakpoint.up('sm')} {
      padding: ${size[24]};
    }
  `,
}

export const containerBackgroundHover: Record<
  Variant,
  (theme: Theme) => SerializedStyles
> = {
  primary: (theme) => css`
    & .banner-icon-wrapper {
      background-color: ${theme.palette.brand[100]};
    }

    &:hover {
      background-color: ${theme.palette.brand[200]};
    }
  `,
  secondary: (theme) => css`
    & .banner-icon-wrapper {
      background-color: ${theme.palette.neutral[100]};
    }

    &:hover {
      background-color: ${theme.palette.neutral[200]};
    }
  `,
  warning: (theme) => css`
    & .banner-icon-wrapper {
      background-color: ${theme.palette.warning[100]};
    }

    &:hover {
      background-color: ${theme.palette.warning[200]};
    }
  `,
  error: (theme) => css`
    & .banner-icon-wrapper {
      background-color: ${theme.palette.error[100]};
    }

    &:hover {
      background-color: ${theme.palette.error[200]};
    }
  `,
}

export const arrow = css`
  grid-area: arrow;
  align-self: center;
`

export const arrowColor: Record<Variant, (theme: Theme) => SerializedStyles> = {
  primary: (theme) => css`
    color: ${theme.palette.brand[500]};
  `,
  secondary: (theme) => css`
    color: ${theme.palette.neutral[700]};
  `,
  warning: (theme) => css`
    color: ${theme.palette.warning[500]};
  `,
  error: (theme) => css`
    color: ${theme.palette.error[500]};
  `,
}

export const arrowSize: Record<Size, SerializedStyles> = {
  small: css`
    width: ${size[24]};
    height: ${size[24]};
  `,
  medium: css`
    width: ${size[24]};
    height: ${size[24]};

    ${breakpoint.up('sm')} {
      width: ${size[32]};
      height: ${size[32]};
    }
  `,
}
